import { PAGE_QUERY_STRING } from "Theconstants";
import { useAppSelector, userSelector } from "context/store";
import React from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { turnSearchParamsIntoObject } from "utils/randomFunctions";

const PaginateMyLife = styled.div<{ disappear?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  @media (max-width: 1090px) {
    display: ${(props) => props.disappear && "none"};
  }
`;

interface ButtonProps {
  forbidden?: boolean;
  active?: boolean;
  onClick?: () => void;
}
const Btn = styled.button<ButtonProps>`
  height: 45px;
  width: 45px;
  border-radius: 5px;
  font-size: var(--fontSmall);
  font-weight: bold;
  border: 1px solid ${(props) => props.theme.border};
  background-color: ${(props) => props.active && props.theme.blue};
  color: ${(props) => props.active && "white"};
  cursor: ${(props) => (props.forbidden ? "not-allowed" : "pointer")};
  &:disabled {
    background-color: #333;
    color: white;
  }
  &:hover:enabled {
    background-color: ${(props) => !props.active && "#c7d6e9"};
  }
  @media (max-width: 600px) {
    height: 28px;
    width: 28px;
  }
`;

let secondRenderOrMore = false;

export const Pagination = ({ disappear }: { disappear?: boolean }) => {
  const { totalPaginatedPages } = useAppSelector(userSelector);

  const [searchParams, setSearchParams] = useSearchParams();

  function render() {
    let middle: React.JSX.Element[] = [];
    for (let ind = page - 3; ind <= totalPaginatedPages; ind++) {
      let currentValue = ind;
      if (ind === page - 3) {
        middle.push(
          <Btn
            onClick={() =>
              setSearchParams({
                ...turnSearchParamsIntoObject(searchParams),
                [PAGE_QUERY_STRING]: String(currentValue - 1),
              })
            }
          >
            ...
          </Btn>
        );
      }
      middle.push(
        <Btn
          onClick={() =>
            setSearchParams({
              ...turnSearchParamsIntoObject(searchParams),
              [PAGE_QUERY_STRING]: String(currentValue),
            })
          }
          active={currentValue === page}
        >
          {currentValue}
        </Btn>
      );
    }
    return middle.map((i, ind) => (
      <React.Fragment key={ind}>{i}</React.Fragment>
    ));
  }

  const page = Number(searchParams.get(PAGE_QUERY_STRING)) || 1;

  //fixed bug where if im in page 11 and change a filter to fetch prods that have less pages, I wouldnt automatically switch to the last page
  if (totalPaginatedPages && totalPaginatedPages < page && secondRenderOrMore) {
    setSearchParams({
      ...turnSearchParamsIntoObject(searchParams),
      [PAGE_QUERY_STRING]: String(totalPaginatedPages),
    });
  }
  secondRenderOrMore = true;

  return (
    <PaginateMyLife disappear={disappear}>
      {/*--------------------------- PRIMER BOTON----------------------------- */}
      <Btn
        disabled={page <= 1}
        forbidden={page <= 1}
        onClick={() =>
          setSearchParams({
            ...turnSearchParamsIntoObject(searchParams),
            [PAGE_QUERY_STRING]: String(page - 1),
          })
        }
      >
        &#10094;
      </Btn>
      {/*-------------------------BOTONES DEL MEDIO-------------------------- */}
      {/* MENOS DE 5 TOTAL PAGINATED PAGES, es el caso + simple */}
      {totalPaginatedPages <= 5 &&
        [...Array(+totalPaginatedPages)].map((_, ind) => (
          <Btn
            onClick={() =>
              setSearchParams({
                ...turnSearchParamsIntoObject(searchParams),
                [PAGE_QUERY_STRING]: String(ind + 1),
              })
            }
            active={ind + 1 === page}
            key={ind}
          >
            {ind + 1}
          </Btn>
        ))}
      {/* MAS DE 5 TOTAL PAGINATED PAGES, aca es donde hay mas lógica*/}
      {totalPaginatedPages > 5 && (
        <>
          {/* page ya avanzada, en las ultimas, sobre el final de la straw */}
          {totalPaginatedPages - page < 4 ? (
            <>
              <FirstBtn
                setSearchParams={setSearchParams}
                searchParams={searchParams}
              />
              {render()}
            </>
          ) : (
            [...Array(4)].map((_, ind) => {
              let currentValue = page + ind;
              let result = (
                <Btn
                  onClick={() =>
                    setSearchParams({
                      ...turnSearchParamsIntoObject(searchParams),
                      [PAGE_QUERY_STRING]: String(currentValue),
                    })
                  }
                  active={currentValue === page}
                  key={ind}
                >
                  {currentValue}
                </Btn>
              );
              if (currentValue === page && page > 4) {
                return (
                  <React.Fragment key={ind}>
                    <FirstBtn
                      setSearchParams={setSearchParams}
                      searchParams={searchParams}
                    />
                    <Btn
                      onClick={() =>
                        setSearchParams({
                          ...turnSearchParamsIntoObject(searchParams),
                          [PAGE_QUERY_STRING]: String(currentValue - 1),
                        })
                      }
                    >
                      ...
                    </Btn>
                    {result}
                  </React.Fragment>
                );
              }
              if (
                ind !== 3 ||
                (ind === 3 && currentValue === totalPaginatedPages)
              ) {
                return result;
              }
              return (
                <React.Fragment key={ind}>
                  {result}
                  <Btn
                    onClick={() =>
                      setSearchParams({
                        ...turnSearchParamsIntoObject(searchParams),
                        [PAGE_QUERY_STRING]: String(currentValue + 1),
                      })
                    }
                  >
                    ...
                  </Btn>
                  <Btn
                    onClick={() =>
                      setSearchParams({
                        ...turnSearchParamsIntoObject(searchParams),
                        [PAGE_QUERY_STRING]: String(totalPaginatedPages),
                      })
                    }
                  >
                    {totalPaginatedPages}
                  </Btn>
                </React.Fragment>
              );
            })
          )}
        </>
      )}

      {/* ULTIMA BOTON */}
      <Btn
        disabled={page >= totalPaginatedPages}
        forbidden={page >= totalPaginatedPages}
        onClick={() =>
          setSearchParams({
            ...turnSearchParamsIntoObject(searchParams),
            [PAGE_QUERY_STRING]: String(page + 1),
          })
        }
      >
        &#10095;
      </Btn>
    </PaginateMyLife>
  );
};

const FirstBtn = ({ setSearchParams, searchParams }) => {
  return (
    <Btn
      onClick={() =>
        setSearchParams({
          ...turnSearchParamsIntoObject(searchParams),
          [PAGE_QUERY_STRING]: 1,
        })
      }
    >
      1
    </Btn>
  );
};
